/**
 * Durable auth-session storage for Supabase.
 *
 * By default Supabase persists the session in `localStorage`. Inside an iOS
 * WKWebView `localStorage` works but lives in the web view's data store, which
 * iOS may purge under storage pressure — logging the user out unexpectedly. On
 * native we therefore back the session with the Capacitor Preferences plugin,
 * which writes to `UserDefaults` and survives eviction and app updates.
 *
 * The object conforms to Supabase's async storage interface
 * (`getItem` / `setItem` / `removeItem`). On the web we return `undefined`
 * from the factory so Supabase falls back to its own `localStorage` default.
 */

import { Preferences } from '@capacitor/preferences';
import { isNativePlatform } from './platform';

interface SupabaseAuthStorage {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
}

const preferencesStorage: SupabaseAuthStorage = {
  async getItem(key) {
    const { value } = await Preferences.get({ key });
    return value ?? null;
  },
  async setItem(key, value) {
    await Preferences.set({ key, value });
  },
  async removeItem(key) {
    await Preferences.remove({ key });
  },
};

/**
 * Returns the storage adapter Supabase should use, or `undefined` on the web
 * (Supabase then uses its built-in localStorage adapter).
 */
export function getAuthStorage(): SupabaseAuthStorage | undefined {
  return isNativePlatform() ? preferencesStorage : undefined;
}
